import {
  Tracer,
  ExplicitContext,
  BatchRecorder,
  // ConsoleRecorder,
  jsonEncoder,
  sampler,
  Annotation,
} from "zipkin";
import { HttpLogger } from "zipkin-transport-http";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { getIsInternalUrl } from "components/Dashboard/Tiles/CustomerTiles/customerTilesUtils";
import { useCallback } from "react";

const recorder = new BatchRecorder({
  logger: new HttpLogger({
    endpoint:
      "https://gke-us-east1.raven-cloud.com/honeycomb-frontend-proxy/api/v1/spans", // production endpoint
    jsonEncoder: jsonEncoder.JSON_V1,
    // headers: {
    // }
  }),
});

// We are aiming to limit the number of Flight memory usage traces to 1 million events/month

// 1 trace per minute
export const MEMORY_SAMPLING_HEARBEAT = 60 * 1000;

// % from 0 - 1
const SAMPLE_RATE = 0.02; // start at 2% sample rate

/**
 * returns a [zipkin-js tracer](https://github.com/openzipkin/zipkin-js) for sending traces to [Honeycomb](https://ui.honeycomb.io/login)
 */
function useTracer(sampleRateOverride?: number, testOnLocalhost = false) {
  const { forceTrace } = useFlagsWithDefaults();

  // don't sample for localhost
  const isLocalhost = getIsInternalUrl(true);

  // if force-trace is ON, sample rate = 1
  const sampleRate =
    isLocalhost && !testOnLocalhost
      ? 0
      : forceTrace
        ? 1
        : sampleRateOverride || SAMPLE_RATE;

  return new Tracer({
    ctxImpl: new ExplicitContext(),
    recorder,
    sampler: new sampler.CountingSampler(sampleRate),
    localServiceName: "flight",
  });
}

export type TraceToHoneycombParams = {
  /** @param {Object} dataToTrace object of binaries (string/number/boolean only) to send; must include a name for the trace */
  dataToTrace: { name: string; [key: string]: string | number | boolean };
  /** @param {Function} callbackFn function to run before sending the span */
  callbackFn?: () => any;
};

/**
 * send a single span to honeycomb
 */
function traceToHoneycomb({
  tracer,
  dataToTrace,
  callbackFn,
  forceTrace,
}: TraceToHoneycombParams & { tracer: any; forceTrace: boolean }): void {
  tracer.scoped(() => {
    const id = tracer.createRootId();
    tracer.setId(id);

    // start of the span
    tracer.recordAnnotation(new Annotation.ClientSend());

    Object.entries({
      "honeycomb.dataset": "Flight",
      serviceName: "flight",
      isForceTraceEnabled: forceTrace,
      flightVersion: process.env.REACT_APP_VERSION,
      ...dataToTrace,
    }).forEach(([key, val]) => {
      tracer.recordBinary(key, val);
    });

    // run the callback if it was passed
    callbackFn?.();

    // end of the span
    tracer.recordAnnotation(new Annotation.ClientRecv());
  });
}

/**
 * returns traceToHoneycomb function with forceTrace included
 * @param sampleRateOverride override the default sample rate e.g. use 1 to trace 100% of the time
 * @param testOnLocalhost allow traces to be send from localhost (usually they are only send from production)
 */
export function useTraceToHoneycomb({
  sampleRateOverride,
  testOnLocalhost,
}: { sampleRateOverride?: number; testOnLocalhost?: boolean } = {}) {
  const { forceTrace } = useFlagsWithDefaults();
  const tracer = useTracer(sampleRateOverride, testOnLocalhost);

  return useCallback(
    ({ dataToTrace, callbackFn }: TraceToHoneycombParams): void =>
      traceToHoneycomb({ dataToTrace, callbackFn, tracer, forceTrace }),
    [tracer, forceTrace]
  );
}
